"use client";

import React from 'react';
import { LocalizedLink } from './LocalizedLink';
import { useTranslations } from '@/hooks/useTranslations';
import { useRouter } from 'next/navigation';
import { clearToken, getToken } from '@/lib/auth';
import { clearSentryUser, setSentryAnonymousUser, setSentryUser } from '@/lib/sentry-user';
import { getApiBaseUrl } from '@/lib/api';
import { useLocale } from '@/contexts/LocaleContext';
import { getPathForLocale } from '@/lib/i18n';

type Profile = {
  displayName?: string | null;
  email?: string | null;
  avatarUrl?: string | null;
};

function getInitials(value: string) {
  const parts = String(value ?? '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  return parts.slice(0, 2).map((part) => part[0]?.toUpperCase()).join('') || 'U';
}

const accountSections = [
  { href: '/account', section: 'profile' },
  { href: '/account?section=favorites', section: 'favorites' },
  { href: '/account?section=favorite-games', section: 'favorite-games' },
  { href: '/account?section=reviews', section: 'reviews' },
  { href: '/account?section=security', section: 'security' },
] as const;

function IconUser() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
      <path d="M20 21a8 8 0 0 0-16 0" />
      <circle cx="12" cy="9" r="4" />
    </svg>
  );
}
function IconHeart() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
      <path d="M20.6 8.5c0 6.1-8.6 11.5-8.6 11.5S3.4 14.6 3.4 8.5c0-2.5 2-4.5 4.5-4.5 1.6 0 3.1.8 4.1 2.1C13 4.8 14.5 4 16.1 4c2.5 0 4.5 2 4.5 4.5Z" />
    </svg>
  );
}
function IconStar() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
      <path d="M12 2.8l2.9 6 6.6.9-4.8 4.6 1.2 6.6L12 17.8 6.1 20.9l1.2-6.6L2.5 9.7l6.6-.9L12 2.8Z" />
    </svg>
  );
}
function IconShield() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
      <path d="M12 2l8 4v6c0 5-3.4 9.4-8 10-4.6-.6-8-5-8-10V6l8-4Z" />
      <path d="M9 12l2 2 4-5" />
    </svg>
  );
}
function IconWrench() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
      <path d="M21 8a6 6 0 0 1-8.4 5.5L6 20l-2-2 6.5-6.6A6 6 0 0 1 16 3l-2.2 2.2 2.8 2.8L21 8Z" />
    </svg>
  );
}
function IconLogOut() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <path d="M16 17l5-5-5-5" />
      <path d="M21 12H9" />
    </svg>
  );
}

function IconGame() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
      <rect x="2" y="6" width="20" height="12" rx="2" />
      <path d="M6 12h4M8 10v4M15 13h.01M18 11h.01" />
    </svg>
  );
}

const sectionIcons: Record<string, React.ReactNode> = {
  profile: <IconUser />,
  favorites: <IconHeart />,
  'favorite-games': <IconGame />,
  reviews: <IconStar />,
  security: <IconShield />,
  admin: <IconWrench />,
};

export function AuthMenu() {
  const { t } = useTranslations();
  const router = useRouter();
  const locale = useLocale();
  const [hasToken, setHasToken] = React.useState(false);
  const [profile, setProfile] = React.useState<Profile | null>(null);
  const [open, setOpen] = React.useState(false);
  const menuRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const loadProfile = async () => {
      const token = getToken();
      if (!token) {
        return;
      }
      try {
        const response = await fetch(`${getApiBaseUrl()}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.ok) {
          const data = (await response.json()) as Profile;
          setProfile(data);
          setSentryUser(data);
        } else if (response.status === 401 || response.status === 403) {
          clearToken();
          setProfile(null);
          clearSentryUser();
        }
      } catch {
        // Ignore profile fetch errors.
      }
    };
    const sync = () => {
      const token = getToken();
      setHasToken(Boolean(token));
      if (!token) {
        setProfile(null);
        setSentryAnonymousUser();
      } else {
        void loadProfile();
      }
    };

    sync();
    window.addEventListener('storage', sync);
    window.addEventListener('auth:token', sync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener('auth:token', sync);
    };
  }, []);

  React.useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [open]);

  const displayName = profile?.displayName || 'Cabinet';
  const initials = getInitials(profile?.displayName || profile?.email || 'User');
  const adminEmails = String(process.env.NEXT_PUBLIC_ADMIN_EMAILS ?? '')
    .split(',')
    .map((item) => item.trim().toLowerCase())
    .filter(Boolean);
  const isAdmin = Boolean(
    profile?.email && adminEmails.includes(String(profile.email).trim().toLowerCase()),
  );

  const handleLogout = () => {
    setOpen(false);
    clearToken();
    clearSentryUser();
    router.push(getPathForLocale(locale, '/'));
    router.refresh();
  };

  if (!hasToken) {
    return (
      <>
        <LocalizedLink href="/login" className="hover:text-white">
          {t('auth.signIn')}
        </LocalizedLink>
        <LocalizedLink
          href="/register"
          className="rounded-full border border-[#ffb74d]/40 px-4 py-1.5 text-[#ffb74d] hover:border-[#ffb74d] hover:bg-[#ffb74d]/10"
        >
          {t('auth.signUp')}
        </LocalizedLink>
      </>
    );
  }

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex h-9 w-9 cursor-pointer items-center justify-center overflow-hidden rounded-full border border-white/10 bg-white/5 text-sm font-semibold text-white hover:border-white/30 hover:bg-white/10"
        aria-label={open ? 'Close menu' : 'Open account menu'}
        aria-expanded={open}
      >
        {profile?.avatarUrl ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={profile.avatarUrl} alt="" className="h-full w-full cursor-pointer object-cover" />
        ) : (
          <span className="text-xs">{initials}</span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-full z-50 mt-2 w-56 rounded-2xl border border-white/10 bg-[#1e2235]/95 shadow-[0_16px_40px_rgba(7,9,20,0.6)] backdrop-blur"
          role="menu"
        >
          <div className="border-b border-white/10 px-4 py-3">
            <p className="truncate text-sm font-semibold text-white">{displayName}</p>
            {profile?.email && (
              <p className="truncate text-xs text-slate-400">{profile.email}</p>
            )}
          </div>
          <nav className="p-2">
            {accountSections.map(({ href, section }) => (
              <LocalizedLink
                key={section}
                href={href}
                onClick={() => setOpen(false)}
                className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-slate-300 hover:bg-white/10 hover:text-white"
                role="menuitem"
              >
                <span className="flex h-5 w-5 items-center justify-center text-current opacity-90">
                  {sectionIcons[section]}
                </span>
                {t(`auth.${section}`)}
              </LocalizedLink>
            ))}
            {isAdmin && (
              <LocalizedLink
                href="/account?section=admin"
                onClick={() => setOpen(false)}
                className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-slate-300 hover:bg-white/10 hover:text-white"
                role="menuitem"
              >
                <span className="flex h-5 w-5 items-center justify-center text-current opacity-90">
                  {sectionIcons.admin}
                </span>
                {t('auth.adminTools')}
              </LocalizedLink>
            )}
          </nav>
          <div className="border-t border-white/10 p-2">
            <button
              type="button"
              onClick={handleLogout}
              className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-rose-400 hover:bg-rose-500/10 hover:text-rose-300"
              role="menuitem"
            >
              <span className="flex h-5 w-5 items-center justify-center">
                <IconLogOut />
              </span>
              {t('auth.signOut')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
